'use client'

import type { Session } from 'next-auth'

import { HeroUIProvider, ToastProvider } from '@heroui/react'
import { ProgressProvider } from '@bprogress/next/app'
import { useReducedMotion } from 'framer-motion'
import { SessionProvider } from 'next-auth/react'
import { ThemeProvider as NextThemesProvider } from 'next-themes'
import { ReactNode } from 'react'
import { User } from '@prisma/client'

import StyledComponentsRegistry from '../lib/registry'
import { ChatBoxIdentities } from '../chat-box/db/identity'

import ContextProvider from '@/context/ContextProvider'

type Props = {
  children: ReactNode
  session: (Session & { expires: string }) | null
  BROADCASTER_CUSTOM_REWARDS: typeCustomReward[]
  BROADCASTER_TWITCH_ACCOUNT: typeTwitchUser
  USER: User | null
  ChatBoxIdentities: ChatBoxIdentities
}

export default function Providers({
  children,
  session,
  BROADCASTER_CUSTOM_REWARDS,
  BROADCASTER_TWITCH_ACCOUNT,
  USER,
  ChatBoxIdentities
}: Props) {
  const reducedMotion = useReducedMotion()

  const prefersReducedMotion =
    typeof window !== 'undefined' ? (reducedMotion ?? false) : false

  return (
    <ProgressProvider
      color="#f2f"
      height="4px"
      options={{
        showSpinner: false
      }}
    >
      <NextThemesProvider
        disableTransitionOnChange
        enableSystem
        attribute="class"
        defaultTheme="system"
      >
        <HeroUIProvider disableRipple disableAnimation={prefersReducedMotion}>
          <ToastProvider
            placement="bottom-center"
            toastProps={{
              radius: 'sm',
              size: 'sm',
              hideCloseButton: true,
              classNames: {
                base: 'border-dashed border-2 flex flex-col'
              }
            }}
          />
          <StyledComponentsRegistry>
            <SessionProvider session={session}>
              <ContextProvider
                BROADCASTER_CUSTOM_REWARDS={BROADCASTER_CUSTOM_REWARDS}
                BROADCASTER_TWITCH_ACCOUNT={BROADCASTER_TWITCH_ACCOUNT}
                ChatBoxIdentities={ChatBoxIdentities}
                USER={USER}
              >
                {children}
              </ContextProvider>
            </SessionProvider>
          </StyledComponentsRegistry>
        </HeroUIProvider>
      </NextThemesProvider>
    </ProgressProvider>
  )
}
